import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  siteThemeColours,
  brandFonts,
  fontSize,
  maxBreakpointQuery,
  visuallyHidden,
} from '../styles';
import { Image, Link, Button } from './ui';
import { formatPrice } from '../utils';

const StyledFeaturedSpaCard = styled.article`
  position: relative;
  animation: fadeUp 1s forwards;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(5%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: ${({ hasDeal, siteTheme, highlighted, alt }) =>
    hasDeal && siteTheme === 'valentines'
      ? siteThemeColours.valentines.tertiary
      : hasDeal && siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.secondary
        : hasDeal && siteTheme === 'summer'
          ? siteThemeColours.summer.primary
          : hasDeal && siteTheme === 'christmas'
            ? siteThemeColours.christmas.secondary
            : hasDeal && siteTheme === 'blackfriday'
              ? standardColours.black
              : highlighted
                ? brandColours.primary
                : alt
                  ? brandColours.senary
                  : standardColours.white};

  ${({ hasDeal, siteTheme, highlighted }) => {
    if (hasDeal && siteTheme === 'valentines') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.valentines.primary};
      `;
    } else if (hasDeal && siteTheme === 'mothersday') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.mothersDay.primary};
      `;
    } else if (hasDeal && siteTheme === 'summer') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${brandColours.tertiary};
      `;
    } else if (hasDeal && siteTheme === 'christmas') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${siteThemeColours.christmas.primary};
      `;
    } else if (hasDeal && siteTheme === 'blackfriday') {
      return css`
        color: ${standardColours.white};
        border: 4px solid ${brandColours.tertiary};
      `;
    } else if (highlighted) {
      return css`
        color: ${standardColours.white};
        border: 2px solid ${brandColours.quinary};
      `;
    }
  }}
`;

const StyledImageWrapper = styled.div`
  position: relative;
`;

const StyledImage = styled(Image)`
  img {
    opacity: 1 !important;
  }
`;

const StyledDeal = styled.p`
  position: absolute;
  top: 0;
  left: 0;
  display: inline-block;
  padding: 6px 12px;
  color: ${standardColours.white};
  ${fontSize(12)};
  background-color: ${({ siteTheme }) =>
    siteTheme === 'valentines'
      ? siteThemeColours.valentines.primary
      : siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.primary
        : siteTheme === 'summer'
          ? brandColours.tertiary
          : siteTheme === 'christmas'
            ? siteThemeColours.christmas.primary
            : siteTheme === 'blackfriday'
              ? brandColours.tertiary
              : brandColours.quinary};
`;

const StyledRating = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 6px 12px;
  ${fontSize(12)};
  color: ${standardColours.white};
  background-color: ${({ hasDeal, siteTheme }) =>
    hasDeal && siteTheme === 'valentines'
      ? siteThemeColours.valentines.tertiary
      : hasDeal && siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.secondary
        : hasDeal && siteTheme === 'summer'
          ? siteThemeColours.summer.primary
          : hasDeal && siteTheme === 'christmas'
            ? siteThemeColours.christmas.secondary
            : hasDeal && siteTheme === 'blackfriday'
              ? standardColours.black
              : brandColours.primary};
`;

const StyledContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 25px 35px 35px;
`;

const StyledSpa = styled.p`
  margin-bottom: 8px;
  color: ${({ hasDeal, siteTheme, highlighted }) =>
    (hasDeal &&
      (siteTheme === 'valentines' ||
        siteTheme === 'mothersday' ||
        siteTheme === 'summer' ||
        siteTheme === 'christmas')) ||
    highlighted
      ? standardColours.white
      : brandColours.quinary};
  ${fontSize(14)};
`;

const StyledName = styled.h3`
  ${brandFonts.secondary};
  ${fontSize(26)};
  line-height: 1.1;

  ${minBreakpointQuery.small`
    ${fontSize(28)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(30)};
  `}
`;

const StyledText = styled.p`
  margin-top: 12px;
  ${fontSize(14)};
  line-height: 1.5;
`;

const StyledPackages = styled.div`
  margin-top: 20px;
`;

const StyledPackagesHeading = styled.h4`
  margin-bottom: 10px;
  ${brandFonts.secondary};
  ${fontSize(20)};
  line-height: 1.1;

  ${minBreakpointQuery.small`
    ${fontSize(22)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}
`;

const StyledPackagesList = styled.ul`
  ${minBreakpointQuery.tiny`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  `}
`;

const StyledPackagesItem = styled.li`
  ${fontSize(14)};

  &:nth-child(2) {
    ${maxBreakpointQuery.tiny`
      ${visuallyHidden()};
    `}
  }
`;

const StyledPackagesFeaturesList = styled.ul`
  display: grid;
  gap: 6px;
  margin-top: 12px;
  list-style-type: disc;
`;

const StyledPackagesFeaturesItem = styled.li`
  margin-left: 14px;
  ${fontSize(12)};
`;

const StyledFacilities = styled.ul`
  margin-top: 14px;
  columns: 2 20px;
  list-style-type: disc;
`;

const StyledFacility = styled.li`
  break-inside: avoid-column;
  margin: 0 0 8px 14px;
  ${fontSize(13)};
`;

const StyledPackagesCount = styled.p`
  margin-top: 18px;
  ${brandFonts.secondary};
  ${fontSize(19)};
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
`;

const StyledPriceButton = styled.div`
  margin-top: auto;
  padding-top: 25px;
`;

const StyledPrice = styled.p`
  margin-bottom: 16px;
  color: ${({ hasDeal, siteTheme, highlighted }) =>
    hasDeal &&
    (siteTheme === 'valentines' ||
      siteTheme === 'mothersday' ||
      siteTheme === 'summer' ||
      siteTheme === 'christmas' ||
      siteTheme === 'blackfriday')
      ? standardColours.white
      : highlighted
        ? brandColours.octonary
        : greyColours.darkerGrey};

  span {
    color: ${({ hasDeal, highlighted }) =>
      hasDeal || highlighted ? standardColours.white : brandColours.primary};
    ${fontSize(18)};

    ${minBreakpointQuery.tiny`
      ${fontSize(20)};
    `}

    ${minBreakpointQuery.small`
      ${fontSize(22)};
    `}
  }
`;

const StyledButton = styled(Button)`
  padding-right: 16px;
  padding-left: 16px;
  white-space: nowrap;

  ${({ hasDeal, siteTheme }) => {
    if (hasDeal && siteTheme === 'valentines') {
      return css`
        background-color: ${siteThemeColours.valentines.primary};
      `;
    } else if (hasDeal && siteTheme === 'christmas') {
      return css`
        background-color: ${siteThemeColours.christmas.primary};
      `;
    }
  }}
`;

const FeaturedSpaCard = ({
  spa: {
    name,
    url,
    images,
    spaRating,
    city,
    county,
    shortDescription,
    facilities,
    packageCount,
    minPrice,
    isPricePerPerson,
    isLuxury,
  },
  packages,
  alt,
  isFeaturedSpaGroups,
  ...props
}) => {
  const {
    spaseekers: {
      promotion: { theme: siteTheme },
    },
  } = useStaticQuery(graphql`
    query FeaturedSpaCardQuery {
      spaseekers {
        promotion {
          theme
        }
      }
    }
  `);

  const packageWithDeal = packages && packages.find(({ deal }) => deal);
  const deal = packageWithDeal && packageWithDeal.deal;

  const highlighted = isLuxury || deal;

  return (
    <StyledFeaturedSpaCard {...props}>
      <StyledInner
        hasDeal={deal}
        siteTheme={siteTheme}
        highlighted={highlighted}
        alt={alt}
      >
        <StyledImageWrapper>
          <StyledImage
            src={images[0].url}
            alt={images[0].title}
            width={575}
            height={280}
            layout="constrained"
          />
          {deal && (
            <StyledDeal siteTheme={siteTheme}>
              {`${
                siteTheme === 'valentines'
                  ? 'Valentines Deal'
                  : siteTheme === 'mothersday'
                    ? 'Mothers Day Deal'
                    : siteTheme === 'summer'
                      ? 'Summer of Spa'
                      : siteTheme === 'christmas'
                        ? 'Festive Season'
                        : siteTheme === 'blackfriday'
                          ? 'Black Friday'
                          : 'Spa Deal'
              } - ${deal.text} ${deal.amount}`}
            </StyledDeal>
          )}
          {spaRating > 0 && (
            <StyledRating hasDeal={deal} siteTheme={siteTheme}>
              Customer Rating {spaRating}/5
            </StyledRating>
          )}
        </StyledImageWrapper>
        <StyledContent>
          <StyledSpa
            hasDeal={deal}
            siteTheme={siteTheme}
            highlighted={highlighted}
          >
            {city}, {county.name}
          </StyledSpa>
          <StyledName as={isFeaturedSpaGroups && 'h4'}>
            <Link to={url}>{name}</Link>
          </StyledName>
          <StyledText>{shortDescription}</StyledText>
          {packages && packages.length > 0 && (
            <StyledPackages>
              <StyledPackagesHeading>Popular Packages:</StyledPackagesHeading>
              <StyledPackagesList>
                {packages.map(({ name, features }, id) => (
                  <StyledPackagesItem key={id}>
                    {name}
                    <StyledPackagesFeaturesList>
                      {features.slice(0, 4).map((feature, id) => (
                        <StyledPackagesFeaturesItem key={id}>
                          {feature}
                        </StyledPackagesFeaturesItem>
                      ))}
                    </StyledPackagesFeaturesList>
                  </StyledPackagesItem>
                ))}
              </StyledPackagesList>
            </StyledPackages>
          )}
          {facilities && facilities.length > 0 && (
            <StyledFacilities>
              {facilities.map((facility, id) => (
                <StyledFacility key={id}>{facility}</StyledFacility>
              ))}
            </StyledFacilities>
          )}
          {packageCount && (
            <StyledPackagesCount>
              {packageCount} Packages Available at this Spa
            </StyledPackagesCount>
          )}
          <StyledPriceButton>
            {minPrice && (
              <StyledPrice
                hasDeal={deal}
                siteTheme={siteTheme}
                highlighted={highlighted}
              >
                From <span>{formatPrice(minPrice)}</span> per{' '}
                {isPricePerPerson ? 'person' : 'package'}
              </StyledPrice>
            )}
            <StyledButton
              to={url}
              state={{ fromFeaturedSpaCard: true }}
              alt={true}
              arrow={true}
              hasDeal={deal}
              siteTheme={siteTheme}
              highlighted={highlighted}
            >
              View details
            </StyledButton>
          </StyledPriceButton>
        </StyledContent>
      </StyledInner>
    </StyledFeaturedSpaCard>
  );
};

export default FeaturedSpaCard;

export const FeaturedSpaCardFragment = graphql`
  fragment LandingFeaturedSpaCardFragment on SPASEEKERS_SpaLandingFeaturedSpa {
    spa {
      name
      url
      images {
        url
        title
      }
      spaRating
      city
      county {
        name
      }
      shortDescription
      minPrice
      isPricePerPerson
      isLuxury
    }
    packages {
      name
      deal {
        text
        amount
      }
      features
    }
  }

  fragment ContentFeaturedSpaCardFragment on SPASEEKERS_ContentBlockFeaturedSpa {
    spa {
      name
      url
      images {
        url
        title
      }
      spaRating
      city
      county {
        name
      }
      shortDescription
      facilities
      packageCount: packages
      minPrice
      isPricePerPerson
      isLuxury
    }
  }

  fragment ContentSpaPackageCardFragment on SPASEEKERS_ContentBlockSpaPackage {
    spa {
      name
      url
      images {
        url
        title
      }
      spaRating
      city
      county {
        name
      }
      shortDescription
      minPrice
      isPricePerPerson
      isLuxury
    }
    packages {
      name
      deal {
        text
        amount
      }
      features
    }
  }
`;
